import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";

/**
 * Props for the DemoViewer component.
 */
interface DemoViewerProps {
  /** Navattic demo ID */
  demoId: string;
}

/**
 * Component that displays a Navattic interactive demo in an iframe.
 * The iframe uses fixed dimensions to ensure Navattic content renders properly.
 * Navattic requires minimum 900px width to display content correctly.
 * Shows a loading spinner while the iframe content is loading.
 *
 * @param props - Component props
 * @returns Navattic demo iframe with loading state
 */
export default function DemoViewer({ demoId }: DemoViewerProps) {
  const [isLoading, setIsLoading] = useState(true);

  // Reset loading state when demoId changes
  useEffect(() => {
    setIsLoading(true);
  }, [demoId]);

  const handleLoad = () => {
    // Add 500ms delay to ensure content is fully visible
    setTimeout(() => {
      setIsLoading(false);
    }, 700);
  };

  return (
    <div className="relative w-full h-[600px] rounded-lg overflow-hidden bg-zinc-50">
      {/* Loading overlay */}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-zinc-50 z-10">
          <Loader2 size={32} className="text-purple-600 animate-spin" />
        </div>
      )}

      <iframe
        src={`https://capture.navattic.com/${demoId}`}
        className="absolute inset-0 w-full h-full border-none"
        data-navattic-demo-id={demoId}
        allow="fullscreen"
        title="Navattic Demo"
        onLoad={handleLoad}
      />
    </div>
  );
}
